import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Users, CheckCircle, Clock, TrendingUp, LogOut, Calendar } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";

interface BookingStats {
  totalLeads: number;
  successfulBookings: number;
  pendingBookings: number;
  failedBookings: number;
}

const Dashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState<BookingStats>({
    totalLeads: 0,
    successfulBookings: 0,
    pendingBookings: 0,
    failedBookings: 0,
  });
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    // Check authentication and admin role
    const checkAuth = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast.error("Please login to access the dashboard");
        navigate("/auth");
        return;
      }

      // Check if user is admin
      const { data: roleData } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", session.user.id)
        .eq("role", "admin")
        .single();

      if (!roleData) {
        toast.error("Access denied. Admin privileges required.");
        navigate("/");
        return;
      }

      setIsAdmin(true);
    };
    checkAuth();

    // Listen for auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (!session) {
        navigate("/auth");
      }
    });

    return () => subscription.unsubscribe();
  }, [navigate]);

  useEffect(() => {
    if (isAdmin) {
      fetchBookingStats();
    }
  }, [isAdmin]);

  const handleLogout = async () => {
    await supabase.auth.signOut();
    toast.success("Logged out successfully");
    navigate("/");
  };

  const fetchBookingStats = async () => {
    try {
      // Get total bookings count
      const { count: totalCount, error: totalError } = await supabase
        .from("bookings")
        .select("*", { count: "exact", head: true });

      if (totalError) throw totalError;

      // Get successful bookings count
      const { count: successCount, error: successError } = await supabase
        .from("bookings")
        .select("*", { count: "exact", head: true })
        .eq("payment_status", "success");

      if (successError) throw successError;

      // Get pending bookings count
      const { count: pendingCount, error: pendingError } = await supabase
        .from("bookings")
        .select("*", { count: "exact", head: true })
        .eq("payment_status", "pending");

      if (pendingError) throw pendingError;

      // Get failed bookings count
      const { count: failedCount, error: failedError } = await supabase
        .from("bookings")
        .select("*", { count: "exact", head: true })
        .eq("payment_status", "failed");

      if (failedError) throw failedError;

      setStats({
        totalLeads: totalCount || 0,
        successfulBookings: successCount || 0,
        pendingBookings: pendingCount || 0,
        failedBookings: failedCount || 0,
      });
    } catch {
      // Error fetching stats - display default values
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="bg-primary text-primary-foreground py-4 px-4 shadow-md">
        <div className="container mx-auto flex items-center justify-between">
          <Button
            variant="ghost"
            onClick={() => navigate("/")}
            className="text-primary-foreground hover:bg-primary-foreground/10"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Home
          </Button>
          <Button
            variant="ghost"
            onClick={handleLogout}
            className="text-primary-foreground hover:bg-primary-foreground/10"
          >
            <LogOut className="mr-2 h-4 w-4" />
            Logout
          </Button>
        </div>
      </header>

      <main className="container mx-auto px-6 py-8">
        <div className="mb-8">
          <h2 className="text-3xl font-bold mb-2">Booking Analytics</h2>
          <p className="text-muted-foreground">
            Track your leads and booking performance
          </p>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[...Array(4)].map((_, i) => (
              <Card key={i} className="animate-pulse">
                <CardHeader className="pb-2">
                  <div className="h-4 bg-muted rounded w-1/2 mb-2"></div>
                </CardHeader>
                <CardContent>
                  <div className="h-10 bg-muted rounded w-1/3"></div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card className="border-l-4 border-l-primary">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                  <Users className="h-4 w-4" />
                  Total Leads
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-4xl font-bold text-primary">
                  {stats.totalLeads}
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  All booking inquiries
                </p>
              </CardContent>
            </Card>

            <Card className="border-l-4 border-l-green-500">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                  <CheckCircle className="h-4 w-4" />
                  Successful Bookings
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-4xl font-bold text-green-600">
                  {stats.successfulBookings}
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  Confirmed & paid
                </p>
              </CardContent>
            </Card>

            <Card className="border-l-4 border-l-yellow-500">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  Pending Bookings
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-4xl font-bold text-yellow-600">
                  {stats.pendingBookings}
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  Awaiting payment
                </p>
              </CardContent>
            </Card>

            <Card className="border-l-4 border-l-red-500">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                  <TrendingUp className="h-4 w-4" />
                  Conversion Rate
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-4xl font-bold">
                  {stats.totalLeads > 0
                    ? Math.round((stats.successfulBookings / stats.totalLeads) * 100)
                    : 0}
                  %
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  Success / Total leads
                </p>
              </CardContent>
            </Card>
          </div>
        )}
      </main>
    </div>
  );
};

export default Dashboard;
